import { Box, Typography, Grid, Card, CardContent, Button, Chip, Paper, Container, Divider } from '@mui/material';
import { Link } from 'react-router-dom';
import { ArrowForward as ArrowForwardIcon, CheckCircle, Star, TrendingUp } from '@mui/icons-material';
import { useMemo } from 'react';

const services = [
  {
    title: 'Term Life Insurance',
    shortDesc: 'Maximum coverage at lowest premium',
    description: 'Get up to ₹2 Crore coverage with Tata AIA term plans. Starting from just ₹500/month.',
    icon: '🛡️',
    color: '#1976d2',
    benefits: ['₹2 Cr Coverage', 'Tax Benefits', '30-Day Free Look'],
    premium: 'From ₹500/month',
    popular: true,
    keywords: 'term plan, term life insurance, Tata AIA term plan, affordable insurance, life protection'
  },
  {
    title: 'ULIP Plans',
    shortDesc: 'Insurance + Investment in one',
    description: 'Grow your wealth while staying protected. Market-linked returns with life cover.',
    icon: '📊',
    color: '#2e7d32',
    benefits: ['Dual Benefit', 'Market Returns', 'Tax Savings'],
    premium: 'From ₹2,000/month',
    popular: false,
    keywords: 'ULIP, unit linked insurance plan, investment insurance, wealth creation, Tata AIA ULIP'
  },
  {
    title: 'Retirement Plans',
    shortDesc: 'Secure your golden years',
    description: 'Build a retirement corpus with guaranteed pension plans. Start early, retire rich.',
    icon: '🏖️',
    color: '#ed6c02',
    benefits: ['Guaranteed Income', 'Retirement Security', 'Flexible Payouts'],
    premium: 'From ₹1,500/month',
    popular: false,
    keywords: 'pension plan, retirement planning, pension insurance, Tata AIA pension, retirement security'
  },
  {
    title: 'Child Plans',
    shortDesc: 'Secure your child\'s future',
    description: 'Education and marriage planning made easy. Guaranteed maturity benefits.',
    icon: '👶',
    color: '#9c27b0',
    benefits: ['Education Fund', 'Guaranteed Returns', 'Waiver Benefit'],
    premium: 'From ₹1,000/month',
    popular: false,
    keywords: 'child plan, education planning, child insurance, future planning'
  },
  {
    title: 'Health Insurance',
    shortDesc: 'Complete health protection',
    description: 'Comprehensive health coverage for you and your family. Cashless treatment.',
    icon: '🏥',
    color: '#d32f2f',
    benefits: ['Cashless Treatment', 'Family Coverage', 'No Claim Bonus'],
    premium: 'From ₹800/month',
    popular: false,
    keywords: 'health insurance, medical insurance, family health cover'
  },
  {
    title: 'Savings Plans',
    shortDesc: 'Guaranteed wealth creation',
    description: 'Traditional savings with guaranteed returns. Build wealth systematically.',
    icon: '💰',
    color: '#0288d1',
    benefits: ['Guaranteed Returns', 'Bonus Additions', 'Loan Facility'],
    premium: 'From ₹1,200/month',
    popular: false,
    keywords: 'savings plan, investment plans, wealth creation, Tata AIA savings, financial growth'
  },
];

const Services = () => {
  // Memoized styles for performance
  const whyChooseUsStyles = useMemo(() => ({
    p: { xs: 3, md: 4 },
    mb: 6,
    bgcolor: 'primary.main',
    color: 'white',
    borderRadius: 4,
    boxShadow: 3,
    position: 'relative',
    overflow: 'hidden',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '200px',
      height: '200px',
      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
      borderRadius: '50%',
      transform: 'translate(50%, -50%)'
    }
  }), []);

  return (
    <Box component="section" aria-labelledby="services-heading" sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip 
            label="🏆 Tata AIA Authorized Agent" 
            sx={{ 
              mb: 3, 
              bgcolor: 'primary.50', 
              color: 'primary.main',
              fontWeight: 600,
              fontSize: '0.9rem',
              px: 2,
              py: 0.5,
              border: '1px solid',
              borderColor: 'primary.200'
            }} 
          />
          
          <Typography 
            id="services-heading"
            variant="h1" 
            component="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 3, 
              color: 'text.primary',
              fontSize: { xs: '2.25rem', md: '3.5rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}
          >
            Best Tata AIA
            <Box component="span" sx={{ display: 'block', color: 'primary.main' }}>
              Insurance Plans
            </Box>
          </Typography>
          
          <Typography 
            variant="h2" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 700, 
              mx: 'auto', 
              mb: 2,
              fontSize: { xs: '1.125rem', md: '1.375rem' },
              lineHeight: 1.5,
              fontWeight: 400
            }}
          >
            Compare plans, get expert advice, and buy the best policy for your needs
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">Free Consultation</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Star sx={{ color: 'warning.main', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">5★ Rated Service</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">Best Premium Rates</Typography>
            </Box>
          </Box>
        </Box>

        {/* Quick Action Banner */}
        <Paper 
          elevation={2} 
          sx={whyChooseUsStyles}
          component="section"
          aria-labelledby="quick-action-heading"
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography 
                id="quick-action-heading"
                variant="h3" 
                sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                🚀 Get Instant Quote in 2 Minutes
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: { xs: 3, md: 0 } }}>
                Compare all Tata AIA plans • Get personalized recommendations • No spam calls
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  minWidth: 200,
                  '&:hover': {
                    bgcolor: 'grey.100',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Get Free Quote
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Services Grid */}
        <Grid 
          container 
          spacing={3}
          component="section"
          aria-labelledby="services-list-heading"
        >
          <Typography 
            id="services-list-heading" 
            variant="h3" 
            sx={{ 
              position: 'absolute', 
              left: '-10000px',
              width: '1px',
              height: '1px',
              overflow: 'hidden'
            }}
          >
            Available Insurance Plans
          </Typography>
          
          {services.map((service, index) => (
            <Grid item key={service.title} xs={12} sm={6} lg={4}>
              <Card 
                component="article"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  border: service.popular ? `2px solid ${service.color}` : '1px solid',
                  borderColor: service.popular ? service.color : 'divider',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px ${service.color}20`
                  },
                  '&:focus-within': {
                    outline: '2px solid',
                    outlineColor: service.color,
                    outlineOffset: '2px'
                  }
                }}
              >
                {/* Popular Badge */}
                {service.popular && (
                  <Chip
                    label="Most Popular"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: service.color,
                      color: 'white',
                      fontWeight: 600,
                      zIndex: 1
                    }}
                  />
                )}

                {/* Icon Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    left: 20,
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: service.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    boxShadow: 3,
                    zIndex: 1
                  }}
                  aria-hidden="true"
                >
                  {service.icon}
                </Box>

                <CardContent sx={{ flexGrow: 1, pt: 5, pb: 3 }}>
                  <Typography 
                    variant="h3" 
                    component="h3" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1, 
                      color: service.color,
                      fontSize: '1.375rem'
                    }}
                  >
                    {service.title}
                  </Typography>

                  <Typography 
                    variant="subtitle1"
                    sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 600,
                      mb: 2,
                      fontSize: '0.95rem'
                    }}
                  >
                    {service.shortDesc}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 3, lineHeight: 1.6 }}
                  >
                    {service.description}
                  </Typography>

                  {/* Premium Display */}
                  <Box sx={{ 
                    bgcolor: `${service.color}10`, 
                    p: 2, 
                    borderRadius: 2, 
                    mb: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: service.color,
                      fontSize: '1.25rem'
                    }}>
                      {service.premium}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Premium starts from
                    </Typography>
                  </Box>

                  {/* Benefits */}
                  <Box sx={{ mb: 3 }} role="list" aria-label={`${service.title} benefits`}>
                    {service.benefits.map((benefit, idx) => (
                      <Chip
                        key={idx}
                        label={benefit}
                        size="small"
                        role="listitem"
                        sx={{
                          mr: 1,
                          mb: 1,
                          bgcolor: `${service.color}15`,
                          color: service.color,
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>

                {/* CTA Buttons */}
                <Box sx={{ p: 3, pt: 0 }}>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: service.color,
                      py: 1.5,
                      fontWeight: 600,
                      borderRadius: 2,
                      mb: 1,
                      '&:hover': {
                        bgcolor: service.color,
                        filter: 'brightness(1.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Get Quote Now
                  </Button>
                  
                  <Button
                    component={Link}
                    to="/services"
                    variant="text"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      color: service.color,
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: `${service.color}10`
                      }
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 8 }} />

        {/* Agent Recruitment CTA */}
        <Box sx={{ textAlign: 'center' }} component="section">
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              color: 'text.primary'
            }}
          >
            Want to Earn by Selling Insurance?
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ mb: 4, maxWidth: 600, mx: 'auto', fontSize: '1.125rem' }}
          >
            Join as a Tata AIA agent and earn attractive commissions. No experience required - we'll train you!
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/join"
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 3,
                bgcolor: 'success.main',
                '&:hover': {
                  bgcolor: 'success.dark',
                  transform: 'translateY(-3px)',
                  boxShadow: 6
                }
              }}
            >
              💼 Become an Agent
            </Button>
            
            <Button
              component={Link}
              to="/contact"
              variant="outlined"
              size="large"
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                '&:hover': {
                  transform: 'translateY(-3px)'
                }
              }}
            >
              📞 Call for Plans
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Services;
